import { Rsp } from "@repo/types";
import { User } from "@repo/types";
import { getHttpClient } from "./http";
import type { QQLoginResult } from "./auth.type";

export const AuthAPI = {
  /**
   * 手机号短信验证码登录
   */
  smsLogin(
    countryCode: string,
    phone: string,
    code: string,
    remember: boolean,
    deviceInfo?: string,
  ) {
    return getHttpClient().post<Rsp<User.Session>>({
      url: "/auth/smsLogin",
      data: {
        country_code: countryCode,
        phone_number: phone,
        verfication_code: code,
        remember,
        device_info: deviceInfo,
      },
    });
  },

  /**
   * 手机号密码登录
   */
  passwordLogin(countryCode: string, phone: string, password: string, deviceInfo?: string) {
    return getHttpClient().post<Rsp<User.Session>>({
      url: "/auth/login",
      data: {
        username: phone,
        password,
        country_code: countryCode,
        device_info: deviceInfo,
      },
    });
  },

  /**
   * 获取短信验证码（登录/注册用）
   */
  getSMSCode(phone: string, countryCode: string, usefor: string) {
    return getHttpClient().get<Rsp<null>>({
      url: `/auth/verfication_code?usefor=${usefor}&phone_number=${phone}&country_code=${countryCode}`,
    });
  },

  /**
   * 获取当前用户手机号绑定的 2FA 短信验证码
   */
  getSMS(usefor: string) {
    return getHttpClient().get<Rsp<{ phone_number: string; country_code: string }>>({
      url: `/auth/f2a?usefor=${usefor}`,
    });
  },

  /**
   * 提交 2FA 短信验证码，获取 session_code
   */
  submitSmsCode(code: string, usefor: string) {
    return getHttpClient().post<Rsp<{ session_code: string }>>({
      url: "/auth/f2a/sms",
      data: { code, usefor },
    });
  },

  /**
   * 提交实名信息获取恢复会话码
   */
  getRecoverySessionByRealID(uid: string, realName: string, idNumber: string, usefor: string) {
    return getHttpClient().post<Rsp<{ session_code: string; uid: string; expire: number }>>({
      url: "/auth/f2a/id",
      data: { uid, real_name: realName, id_number: idNumber, usefor },
    });
  },

  /**
   * QQ 授权码登录
   */
  qqLogin(authorizationCode: string, redirectUri: string, deviceInfo: string) {
    return getHttpClient().post<Rsp<QQLoginResult>>({
      url: "/auth/qq/login",
      data: {
        authorization_code: authorizationCode,
        redirect_uri: redirectUri,
        device_info: deviceInfo,
      },
    });
  },

  /**
   * QQ 登录新用户注册（绑定手机号）
   */
  qqRegister(ticket: string, countryCode: string, phoneNumber: string, verificationCode: string) {
    return getHttpClient().post<Rsp<User.Session>>({
      url: "/auth/qq/register",
      data: {
        ticket,
        country_code: countryCode,
        phone_number: phoneNumber,
        verification_code: verificationCode,
      },
    });
  },

  /**
   * 重置密码
   */
  updatePassword(password: string, sessionCode: string, uid: string) {
    return getHttpClient().post<Rsp<null>>({
      url: "/auth/v2/reset_password",
      data: { password, session_code: sessionCode, uid },
    });
  },

  /**
   * 退出登录，注销当前设备会话
   */
  signOut() {
    return getHttpClient().get<Rsp<string>>({
      url: "/auth/signout",
    });
  },

  /**
   * 获取当前账号的登录设备列表，is_current 标记本机
   */
  getLoginDevices() {
    return getHttpClient().get<Rsp<User.LoginDevice[]>>({
      url: "/auth/devices",
    });
  },

  /**
   * 注销指定登录设备（本机除外），被注销设备将被踢下线
   * @param sid 设备会话 ID
   */
  kickLoginDevice(sid: string) {
    return getHttpClient().delete<Rsp<null>>({
      url: `/auth/devices/${encodeURIComponent(sid)}`,
    });
  },

  /**
   * 注销除本机外的全部登录设备，返回被注销的设备数量
   */
  kickOtherLoginDevices() {
    return getHttpClient().delete<Rsp<number>>({
      url: "/auth/devices",
    });
  },
};
